import { mealTypeOf } from "./mealTypes";
/**
 * The shape of a meal as the plan surfaces render it, plus the non-cook night
 * labels. Issue #1093.
 *
 * ⚠️ THIS LIVES IN src/lib, NOT IN A COMPONENT, ON PURPOSE. It was briefly
 * exported from FridgeCard.tsx and imported by src/lib/mealPlanText.ts — a
 * lib → component import, which is backwards, and it broke the production
 * build: vite-plugin-pwa failed its service-worker step with ENOENT on
 * dist/sw.js. `npm run build` caught it and vitest did not, which is exactly
 * why the build gate is non-negotiable (CLAUDE.md Track 1, step 2).
 *
 * Both the fridge card (#1092) and the shareable text (#1093) read from here so
 * the two surfaces cannot drift on what "Thursday is takeout" looks like.
 */

export interface PlanMeal {
  day?: string;
  meal_name?: string;
  meal?: string;
  meal_type?: string;
  /** School lunch slots (#1106's school_lunch schema). */
  main?: string;
  fruit?: string;
  snack?: string;
  drink?: string;
  /** Per-kid rows, when school-lunch rules differ (#1107). */
  per_kid?: Array<{ kid: string; main?: string; note?: string }>;
  /** Night type from #1091 — absent until that ships; handled either way. */
  night_type?: string;
  night_reason?: string;
  removed?: boolean;
}

/** How a non-cook night reads. Shared so the card and the text agree. */
export const NON_COOK_LABEL: Record<string, string> = {
  takeout: "🥡 takeout",
  fast_food: "🍔 fast food",
  leftovers: "♻️ leftovers",
};

/**
 * Does this meal occupy the (day, meal type) slot being acted on? — #1176.
 *
 * ⚠️ THIS IS A DELIBERATE MIRROR of `isSameSlot` in
 * `supabase/functions/meal-plan-swap-night/mealSwapDiff.ts`, pinned by a parity
 * test in `src/lib/__tests__/mealSlotParity.test.ts`. The Deno module has
 * `.ts`-suffixed specifiers Vite cannot bundle into the app, so rather than
 * contort either side into importing across that boundary, both declare the
 * rule and a test asserts they agree on every case.
 *
 * The rule has to be identical in both places because the server rewrites the
 * stored plan and the client rewrites what she is looking at. If they disagree,
 * the screen shows a plan the database does not hold — and she has no way to
 * tell which one is real.
 *
 * ⚠️ THE UNTAGGED CASE IS LOAD-BEARING, NOT A CONVENIENCE. A meal in a run that
 * predates #1106 has no `meal_type`. Requiring an exact match would make every
 * old plan silently unswappable — the button would work and change nothing.
 */
/**
 * A stable key for one SLOT in a plan — a day plus a meal type.
 *
 * A plan holds up to four meals for the same day (#1106), so anything keyed by
 * day alone addresses all of them at once. That is what made a single lunch
 * swap show a spinner on that day's dinner row too.
 *
 * Untagged meals (pre-#1106 plans) collapse to "dinner", matching how
 * `mealTypeOf` and FridgeCard already read them.
 */
/**
 * Apply a patch to ONE slot in a plan, leaving every other meal untouched.
 *
 * ⚠️ THIS EXISTS BECAUSE ADDRESSING BY DAY CORRUPTED PLANS. A plan holds up to
 * four meals for the same day (#1106). Both the "remove this night" and "set
 * the night type" handlers mapped on `m.day === day`, so removing Monday's
 * lunch also removed Monday's dinner — silently, in the stored plan, with
 * nothing on screen to say which meal had actually been cancelled.
 *
 * Extracted so the rule is testable on its own rather than only reachable
 * through a 20k-line component. Uses `isSameMealSlot`, which is parity-tested
 * against the server's copy, so the client and the cron agree on which meal a
 * write lands on.
 */
export function applyToMealSlot<T extends Pick<PlanMeal, "day" | "meal_type">>(
  meals: readonly T[],
  day: string,
  mealType: string | undefined,
  patch: Partial<T>,
): T[] {
  return meals.map((m) => (isSameMealSlot(m, day, mealType) ? { ...m, ...patch } : m));
}

export function mealSlotKey(day: string, mealType?: string | null): string {
  return `${day}|${mealType || "dinner"}`;
}

/**
 * ⚠️ #1547 — the meal's tag is FOLDED before comparing, not tested for
 * truthiness. This must stay byte-for-byte equivalent to `isSameSlot` in
 * `supabase/functions/meal-plan-swap-night/mealSwapDiff.ts`;
 * `mealSlotParity.test.ts` compares the two on every case and is the only
 * thing making the duplication safe.
 *
 * An untagged legacy meal still folds to `dinner`, so a dinner swap keeps
 * matching it — the case the parity test calls load-bearing, because requiring
 * an exact match would make every pre-#1106 plan silently unswappable. What
 * changes is that an empty or non-canonical tag no longer matches a swap for
 * EVERY type.
 */
export function isSameMealSlot(
  meal: Pick<PlanMeal, "day" | "meal_type">,
  day: string,
  mealType?: string
): boolean {
  if (meal.day !== day) return false;
  if (!mealType) return true;
  return mealTypeOf(meal) === mealTypeOf({ meal_type: mealType });
}
